"use client";

import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/auth-context";
import { sessionKeys } from "@/lib/sessionKeys";

const ACCOUNTS_ENDPOINT =
  "/api/social/accounts?types=tiktok,tiktok_business,youtube,instagram_direct,instagram_via_facebook";

const SESSION_KEY: Record<string, (id: string) => string> = {
  tiktok:                 sessionKeys.tiktokIndexed,
  tiktok_business:        sessionKeys.tiktokIndexed,
  youtube:                sessionKeys.youtubeIndexed,
  instagram_direct:       sessionKeys.igIndexed,
  instagram_via_facebook: sessionKeys.igIndexed,
};

const INDEX_ENDPOINT: Record<string, string> = {
  tiktok:                 "/api/index-tiktok",
  tiktok_business:        "/api/index-tiktok",
  youtube:                "/api/youtube/index-youtube",
  instagram_direct:       "/api/instagram/index-noparent",
  instagram_via_facebook: "/api/instagram/index-noparent",
};

export function useSessionAutoIndex() {
  const { user, isInitialized } = useAuth();
  const ranRef = useRef(false);

  useEffect(() => {
    if (!isInitialized || !user) return;
    if (ranRef.current) return;
    ranRef.current = true;

    (async () => {
      try {
        const res = await fetch(ACCOUNTS_ENDPOINT, { cache: "no-store" });
        if (!res.ok) {
          console.warn("[AUTO-INDEX:GLOBAL] failed to fetch accounts", { status: res.status });
          return;
        }
        const data = await res.json();
        const accounts: any[] = data?.accounts ?? [];

        for (const account of accounts) {
          const { id, connectionId, connectionType, isConnectionOnly } = account;
          if (isConnectionOnly) continue;

          const sessionKeyFn = SESSION_KEY[connectionType];
          const endpoint = INDEX_ENDPOINT[connectionType];
          if (!sessionKeyFn || !endpoint) continue;

          const sessionKey = sessionKeyFn(id);
          try {
            if (sessionStorage.getItem(sessionKey) === "true") {
              console.log("[AUTO-INDEX:GLOBAL] already indexed this session, skipping", {
                accountId: id,
                connectionType,
              });
              continue;
            }
          } catch {}

          console.log("[AUTO-INDEX:GLOBAL] triggering auto-index", { accountId: id, connectionType });
          try { sessionStorage.setItem(sessionKey, "true"); } catch {}

          fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ connectionId }),
          })
            .then((resp) => {
              if (!resp.ok) {
                console.error("[AUTO-INDEX:GLOBAL] index request failed", {
                  accountId: id,
                  connectionType,
                  status: resp.status,
                });
                try { sessionStorage.removeItem(sessionKey); } catch {}
              } else {
                console.log("[AUTO-INDEX:GLOBAL] index triggered successfully", {
                  accountId: id,
                  connectionType,
                });
              }
            })
            .catch((err) => {
              console.error("[AUTO-INDEX:GLOBAL] index request error", {
                accountId: id,
                connectionType,
                error: err,
              });
              try { sessionStorage.removeItem(sessionKey); } catch {}
            });
        }
      } catch (err) {
        console.error("[AUTO-INDEX:GLOBAL] error fetching accounts", err);
        ranRef.current = false; // allow retry on next render
      }
    })();
  }, [isInitialized, user]);
}
